import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Pill, TableSkeleton } from "../components/Pill";
import { COMPLETENESS_STATUS_UI, TRUTH_SOURCE_UI } from "../lib/enumUI";
import { projectsQueryOptions } from "./queries";
import type { ProjectSummary } from "./types";

export function ProjectList() {
  const { data, isLoading, error } = useQuery(projectsQueryOptions);

  if (isLoading) {
    return (
      <div className="page">
        <TableSkeleton />
      </div>
    );
  }
  if (error) return <ErrorBlock error={error} />;

  const projects = data?.projects ?? [];

  return (
    <div className="page">
      <h1>Projects</h1>
      {projects.length === 0 ? (
        <p className="empty-state">No projects visible under your access.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Type</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Truth source</th>
              <th>Completeness</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <ProjectRow key={p.id} project={p} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ProjectRow({ project }: { project: ProjectSummary }) {
  return (
    <tr>
      <td>
        <Link to="/projects/$projectId" params={{ projectId: project.id }}>
          {project.display_reference}
        </Link>
      </td>
      <td>{project.project_type_code}</td>
      <td>{project.lifecycle_stage_code}</td>
      <td>{project.status}</td>
      <td>
        {project.truth_source_type
          ? TRUTH_SOURCE_UI[project.truth_source_type].label
          : "—"}
      </td>
      <td>
        <Pill tone={COMPLETENESS_STATUS_UI[project.completeness_status].tone}>
          {COMPLETENESS_STATUS_UI[project.completeness_status].label}
        </Pill>
      </td>
      <td>{new Date(project.updated_at).toLocaleString()}</td>
    </tr>
  );
}

function ErrorBlock({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="page">
      <p className="alert" role="alert">
        Failed to load projects: {message}
      </p>
    </div>
  );
}
